import { type IssueData, z } from 'zod';
import type { Estate } from '@/features/estates-property/api/estate';
import { i18n } from '@/lib/i18n';

const getRequiredFieldSchema = (field: string) =>
  i18n.t('required-field', { field: i18n.t(field) });

const getRequiredStringFieldSchema = (field: string) =>
  z
    .string({
      required_error: getRequiredFieldSchema(field),
      invalid_type_error: getRequiredFieldSchema(field),
    })
    .min(1, getRequiredFieldSchema(field));

const normalizeNumber = (value: unknown) => {
  if (typeof value !== 'string') {
    return value;
  }

  const normalizedValue = value.trim().replace(',', '.');

  if (!normalizedValue) {
    return undefined;
  }

  const parsedValue = Number(normalizedValue);

  return Number.isNaN(parsedValue) ? value : parsedValue;
};

const getRequiredNumberSchema = (field: string) =>
  z.number({
    required_error: getRequiredFieldSchema(field),
    invalid_type_error: getRequiredFieldSchema(field),
  });

const getRequiredNumberFieldSchema = (field: string, schema = getRequiredNumberSchema(field)) =>
  z.preprocess(normalizeNumber, schema);

export const getEstateValidationSchema = () => {
  const estateSchema = z
    .object({
      name: z
        .string()
        .max(60, i18n.t('fieldMustBeAtMostCharacters', { field: i18n.t('name'), max: 60 })),
      address: z.object({
        address: getRequiredStringFieldSchema('address'),
        houseNumber: getRequiredStringFieldSchema('houseNumber'),
        street: getRequiredStringFieldSchema('street'),
        postalCode: getRequiredStringFieldSchema('postalCode'),
        city: getRequiredStringFieldSchema('city'),
        countryId: getRequiredStringFieldSchema('country'),
        lat: getRequiredNumberFieldSchema('latitude'),
        long: getRequiredNumberFieldSchema('longitude'),
      }),
      estateDetails: z.object({
        marketType: getRequiredStringFieldSchema('marketType'),
        propertyType: z.enum(['house', 'apartment'], {
          errorMap: () => ({
            message: i18n.t('analyticsOnlyForSpecifiedPropertyTypes'),
          }),
        }),
        buildingYear: getRequiredNumberFieldSchema('buildingYear'),
      }),
      estateAreasRooms: z.object({
        livingArea: getRequiredNumberFieldSchema(
          'livingArea',
          getRequiredNumberSchema('livingArea')
            .min(10, i18n.t('fieldMustBeAtLeast', { field: i18n.t('livingArea'), min: 10 }))
            .max(800, i18n.t('fieldMustBeAtMost', { field: i18n.t('livingArea'), max: 800 }))
        ),
        totalNumberOfRooms: getRequiredNumberFieldSchema(
          'totalNumberOfRooms',
          getRequiredNumberSchema('totalNumberOfRooms')
            .min(1, getRequiredFieldSchema('totalNumberOfRooms'))
            .max(
              100,
              i18n.t('fieldMustBeAtMost', { field: i18n.t('totalNumberOfRooms'), max: 100 })
            )
        ),
        numberOfBathrooms: getRequiredNumberFieldSchema(
          'numberOfBathrooms',
          getRequiredNumberSchema('numberOfBathrooms')
            .min(1, i18n.t('fieldMustBeAtLeast', { field: i18n.t('numberOfBathrooms'), min: 1 }))
            .max(5, i18n.t('fieldMustBeAtMost', { field: i18n.t('numberOfBathrooms'), max: 5 }))
        ),
        balconyArea: getRequiredNumberFieldSchema(
          'balconyArea',
          getRequiredNumberSchema('balconyArea').max(
            200,
            i18n.t('fieldMustBeAtMost', { field: i18n.t('balconyArea'), max: 200 })
          )
        ),
        gardenArea: getRequiredNumberFieldSchema(
          'gardenArea',
          getRequiredNumberSchema('gardenArea').max(
            200,
            i18n.t('fieldMustBeAtMost', { field: i18n.t('gardenArea'), max: 200 })
          )
        ),
        plotArea: getRequiredNumberFieldSchema('plotArea').optional(),
        floor: z.preprocess(normalizeNumber, z.number().optional().nullable()),
        hasElevator: z.preprocess(normalizeNumber, z.number().optional().nullable()),
      }),
      estateEnergyDetails: z.object({
        hwbClass: getRequiredStringFieldSchema('hwbClass'),
      }),
      estateRatings: z.object({
        kitchenQuality: getRequiredNumberFieldSchema('kitchen'),
        kitchenCondition: getRequiredNumberFieldSchema('kitchen'),
        bathroomQuality: getRequiredNumberFieldSchema('bathroom'),
        bathroomCondition: getRequiredNumberFieldSchema('bathroom'),
        flooringQuality: getRequiredNumberFieldSchema('flooring'),
        flooringCondition: getRequiredNumberFieldSchema('flooring'),
        windowsQuality: getRequiredNumberFieldSchema('windows'),
        windowsCondition: getRequiredNumberFieldSchema('windows'),
      }),
    })
    .superRefine(({ estateAreasRooms, estateDetails }, ctx) => {
      if (estateDetails.propertyType === 'apartment') {
        if (typeof estateAreasRooms.floor !== 'number') {
          ctx.addIssue({
            message: getRequiredFieldSchema('floor'),
            path: ['estateAreasRooms', 'floor'],
          } as IssueData);
        }
        if (typeof estateAreasRooms.hasElevator !== 'number') {
          ctx.addIssue({
            message: getRequiredFieldSchema('hasElevator'),
            path: ['estateAreasRooms', 'hasElevator'],
          } as IssueData);
        }
      } else if (estateDetails.propertyType === 'house') {
        if (typeof estateAreasRooms.plotArea !== 'number') {
          ctx.addIssue({
            message: getRequiredFieldSchema('plotArea'),
            path: ['estateAreasRooms', 'plotArea'],
          } as IssueData);
        } else if (estateAreasRooms.plotArea < 50) {
          ctx.addIssue({
            message: i18n.t('fieldMustBeAtLeast', { field: i18n.t('plotArea'), min: 50 }),
            path: ['estateAreasRooms', 'plotArea'],
          } as IssueData);
        } else if (estateAreasRooms.plotArea > 5000) {
          ctx.addIssue({
            message: i18n.t('fieldMustBeAtMost', { field: i18n.t('plotArea'), max: 5000 }),
            path: ['estateAreasRooms', 'plotArea'],
          } as IssueData);
        }
      }
    });

  return estateSchema;
};

export type EstateValidationSchemaType = z.infer<ReturnType<typeof getEstateValidationSchema>>;

export const transformEstate = (estate: Estate) => ({
  ...estate,
  name: estate.name || '',
  address: {
    ...estate.address,
    address: estate.address?.address || '',
    houseNumber: estate.address?.houseNumber || '',
    street: estate.address?.street || '',
    postalCode: estate.address?.postalCode || '',
    city: estate.address?.city || '',
    countryId: estate.address?.country?.id || '',
  },
  estateDetails: {
    ...estate.estateDetails,
    buildingYear: estate.estateDetails?.buildingYear
      ? Number(estate.estateDetails?.buildingYear)
      : undefined,
  },
  estateAreasRooms: {
    ...estate.estateAreasRooms,
    numberOfFloors: estate.estateAreasRooms?.numberOfFloors,
    hasElevator: estate.estateAreasRooms?.hasElevator,
    livingArea: estate.estateAreasRooms?.livingArea,
    balconyArea: estate.estateAreasRooms?.balconyArea,
    gardenArea: estate.estateAreasRooms?.gardenArea,
    totalNumberOfRooms: estate.estateAreasRooms?.totalNumberOfRooms,
    numberOfBathrooms: estate.estateAreasRooms?.numberOfBathrooms,
    plotArea: estate.estateAreasRooms?.plotArea,
  },
  estateEnergyDetails: {
    ...estate.estateEnergyDetails,
  },
  estateRatings: {
    ...estate.estateRatings,
  },
});
